import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles } from '@material-ui/core';
import { isEqual } from 'lodash';
import React, { useState } from 'react';

import { useUpdateTaskMutation } from '../../../../graphql/gen/types';
import { LabelInput } from '../../common/LabelInput';
import { CardLabels } from '../../feed/CardLabels';
import { canEditTask } from '../../permissions';
import { TaskField } from './common/TaskField';

type Props = {
  id: string;
  labels: { id: string; name: string }[];
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type State = { type: 'initial' } | { type: 'editing'; labelIds: string[] };

type PlexTaskTitleViewProps =
  | {
      mode: 'edit';
      saving: boolean;
      value: string[];
      onEdit: () => void;
      onChange: (value: string[]) => void;
    }
  | {
      mode: 'view';
      value: { id: string; name: string }[];
      onEdit: (() => void) | null;
    };

export const TaskLabelsView = React.forwardRef(function PlexTaskTitleView(
  props: PlexTaskTitleViewProps,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement {
  const classes = useStyles();

  return (
    <div ref={ref}>
      {props.mode === 'view' && (
        <TaskField mode="view" label="Labels" onClick={props.onEdit}>
          {props.value.length === 0 && 'None'}
          <CardLabels labels={props.value} />
        </TaskField>
      )}
      {props.mode === 'edit' && (
        <TaskField mode="edit" onClickAway={props.onEdit} saving={props.saving}>
          <LabelInput
            className={classes.labelInput}
            onLabelsListChanged={props.onChange}
            variant="outlined"
            selectedLabelIds={props.value}
            centeredLabel
            autocompleteProps={{
              // disable portal to ensure ClickAwayListener does not reset the editable state
              disablePortal: true,
            }}
            inputProps={{
              autoFocus: true,
            }}
          />
        </TaskField>
      )}
    </div>
  );
});

export const TaskLabels = ({ id, plexRelation, taskRelation, labels }: Props): React.ReactElement => {
  const [state, setState] = useState<State>({ type: 'initial' });
  const [updateTask, { loading }] = useUpdateTaskMutation();

  if (state.type === 'initial') {
    return (
      <TaskLabelsView
        mode="view"
        value={labels}
        onEdit={
          canEditTask({ plexRelation, taskRelation })
            ? () => {
                setState({ type: 'editing', labelIds: labels.map((label) => label.id) });
              }
            : null
        }
      />
    );
  } else {
    return (
      <TaskLabelsView
        mode="edit"
        saving={loading}
        value={state.labelIds}
        onChange={(labelIds: string[]) => {
          setState({ ...state, labelIds });
        }}
        onEdit={async () => {
          if (!isEqual(new Set([...labels.map((label) => label.id)]), new Set(state.labelIds))) {
            await updateTask({ variables: { task: { id, labelIds: state.labelIds } } });
          }

          setState({ type: 'initial' });
        }}
      />
    );
  }
};

const useStyles = makeStyles(() =>
  createStyles({
    labelInput: {
      flex: 1,
    },
  })
);
